// Design System LoadingSpinner Component
// Various loading indicators

import React from 'react';
import { 
  CircularProgress, 
  LinearProgress, 
  Box, 
  Typography,
  styled 
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens } from '../../tokens';

// Styled components
const LoadingContainer = styled(Box)(({ theme, fullScreen }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacingTokens.component.md,
  
  ...(fullScreen && {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.7)'
      : 'rgba(255, 255, 255, 0.9)',
    zIndex: theme.zIndex.modal,
  }),
}));

/**
 * LoadingSpinner - Circular loading indicator
 * 
 * @example
 * // Basic spinner
 * <LoadingSpinner />
 * 
 * // With label
 * <LoadingSpinner label="Loading data..." />
 * 
 * // Custom size and color
 * <LoadingSpinner size={60} color="secondary" />
 */
export const LoadingSpinner = ({
  size = 40,
  color = 'primary',
  label,
  thickness = 3.6,
  ...props
}) => {
  return (
    <LoadingContainer>
      <CircularProgress
        size={size}
        color={color}
        thickness={thickness}
        {...props}
      />
      {label && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
    </LoadingContainer>
  );
};

LoadingSpinner.propTypes = {
  size: PropTypes.number,
  color: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success', 'inherit']),
  label: PropTypes.string,
  thickness: PropTypes.number,
};

/**
 * LoadingBar - Linear progress indicator
 * 
 * @example
 * // Indeterminate progress
 * <LoadingBar />
 * 
 * // With progress value
 * <LoadingBar value={75} />
 * 
 * // With buffer
 * <LoadingBar variant="buffer" value={60} valueBuffer={80} />
 */
export const LoadingBar = ({
  variant = 'indeterminate',
  value,
  valueBuffer,
  color = 'primary',
  height = 4,
  ...props
}) => {
  return (
    <LinearProgress
      variant={variant}
      value={value}
      valueBuffer={valueBuffer}
      color={color}
      sx={{ height }}
      {...props}
    />
  );
};

LoadingBar.propTypes = {
  variant: PropTypes.oneOf(['determinate', 'indeterminate', 'buffer', 'query']),
  value: PropTypes.number,
  valueBuffer: PropTypes.number,
  color: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success', 'inherit']),
  height: PropTypes.number,
};

/**
 * PageLoader - Full page loading overlay
 * 
 * @example
 * <PageLoader />
 * 
 * // With custom message
 * <PageLoader label="Preparing your dashboard..." />
 */
export const PageLoader = ({ label = 'Loading...' }) => {
  return (
    <LoadingContainer fullScreen>
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" color="text.primary">
        {label}
      </Typography>
    </LoadingContainer>
  );
};

PageLoader.propTypes = {
  label: PropTypes.string,
};

/**
 * ContentLoader - Loading state for content areas
 * 
 * @example
 * <ContentLoader height={200} />
 */
export const ContentLoader = ({ height = 400, label }) => {
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme => theme.palette.action.hover,
        borderRadius: 2,
      }}
    >
      <LoadingSpinner label={label} />
    </Box>
  );
};

ContentLoader.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
};

// Default export
export default LoadingSpinner;